import React, { useEffect, useState, useRef, memo, useCallback } from "react";
import MiniTitle from "layouts/MiniTitle";
import MainTitle from "layouts/MainTitle";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import { motion, useInView, AnimatePresence } from "framer-motion";
import "swiper/css";
import "styles/public.css";
import devFallback from "assets/layout_img/teacher.jpg";
import logo from "assets/layout_img/logo.svg";
import { FiArrowLeftCircle, FiArrowRightCircle } from "react-icons/fi";
import {
  FaLinkedinIn,
  FaTelegramPlane,
  FaInstagram,
  FaGithub,
  FaPhone,
} from "react-icons/fa";

const BASE_URL = "https://algoritm-api-edu.uz/";
const API_URL = `${BASE_URL}api/public/MainPageUi/GetAllDevelopers`;

const SocialIcon = memo(({ href, icon, label }) => {
  const isDisabled = !href || href.trim() === "";

  return (
    <motion.a
      href={isDisabled ? "#" : href}
      target={isDisabled ? "" : "_blank"}
      rel={isDisabled ? "" : "noopener noreferrer"}
      className={`social-icon ${isDisabled ? "disabled" : "active"}`}
      onClick={(e) => isDisabled && e.preventDefault()}
      whileHover={
        !isDisabled ? { scale: 1.3, rotate: [0, -15, 15, -15, 0] } : {}
      }
      whileTap={!isDisabled ? { scale: 0.9 } : {}}
      aria-label={label}
      title={label}
    >
      {icon}
    </motion.a>
  );
});
SocialIcon.displayName = "SocialIcon";

const PhoneTooltip = memo(({ phoneNumber }) => {
  if (!phoneNumber) return null;

  return (
    <div className="phone-tooltip-wrapper">
      <div className="phone-icon">
        <FaPhone size={20} />
      </div>
      <div className="phone-tooltip">
        <div className="tooltip-logo">
          <img src={logo} alt="Algoritm" loading="lazy" />
        </div>
        <span>{phoneNumber}</span>
        <div className="tooltip-arrow"></div>
      </div>
    </div>
  );
});
PhoneTooltip.displayName = "PhoneTooltip";

const DeveloperCard = memo(({ dev, isActive, onClick }) => {
  return (
    <motion.div
      className={`teacher-card ${isActive ? "active" : "inactive"}`}
      onClick={onClick}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.98 }}
      animate={{
        scale: isActive ? 1.12 : 0.92,
        opacity: isActive ? 1 : 0.75,
        filter: isActive ? "brightness(1)" : "brightness(0.8)",
      }}
      transition={{ duration: 0.6, ease: "easeOut" }}
    >
      <div className="teacher-image-wrapper">
        {isActive && (
          <motion.div
            className="active-glow"
            layoutId={`glow-${dev.id}`}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1.2 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
          />
        )}
        <img
          src={dev.imageUrl || devFallback}
          alt={dev.fullName}
          className="developer-img"
          loading="lazy"
          onError={(e) => {
            e.target.src = devFallback;
          }}
        />
      </div>
    </motion.div>
  );
});
DeveloperCard.displayName = "DeveloperCard";

const Developers = memo(() => {
  const [developers, setDevelopers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);

  const swiperRef = useRef(null);
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, margin: "-100px" });

  const fetchDevelopers = useCallback(async () => {
    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          search: "",
          sortBy: "",
          orderType: "",
          pageSize: 50,
          page: 1,
        }),
      });

      if (!res.ok) throw new Error("Server xatosi");

      const data = await res.json();
      const rows = data?.rows || [];

      const formatted = rows.map((d) => ({
        id: d.id || Math.random().toString(36),
        fullName: d.fullName?.trim() || "Ism mavjud emas",
        position: d.position?.trim() || "Lavozim ko‘rsatilmagan",
        bio: (
          d.description ||
          d.bio ||
          "Bu dasturchi haqida ma'lumot kiritilmagan."
        ).trim(),
        imageUrl: d.imageUrl ? BASE_URL + d.imageUrl : null,
        linkedinUrl: d.linkedinUrl || null,
        telegramUsername: d.telegramUsername || null,
        instagramUrl: d.instagramUrl || null,
        gitUrl: d.gitUrl || null,
        phoneNumber: d.phoneNumber || null,
      }));

      setDevelopers(formatted);
      setActiveIndex(0);
    } catch (err) {
      console.error("Dasturchilar yuklanmadi:", err);
      setDevelopers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDevelopers();
  }, [fetchDevelopers]);

  const handleSlideChange = useCallback((swiper) => {
    setActiveIndex(swiper.realIndex);
  }, []);

  const goNext = useCallback(() => swiperRef.current?.swiper?.slideNext(), []);
  const goPrev = useCallback(() => swiperRef.current?.swiper?.slidePrev(), []);

  if (loading) {
    return (
      <section className="teacher-section" ref={containerRef}>
        <div className="teacher-container">
          <MiniTitle>Dasturchilarimiz</MiniTitle>
          <MainTitle>Yuklanmoqda...</MainTitle>
          <div className="skeleton-carousel" />
        </div>
      </section>
    );
  }

  if (!developers.length) {
    return (
      <section className="teacher-section" ref={containerRef}>
        <div className="teacher-container">
          <MiniTitle>Dasturchilarimiz</MiniTitle>
          <MainTitle>Hozircha ma'lumot mavjud emas</MainTitle>
        </div>
      </section>
    );
  }

  const current = developers[activeIndex];

  return (
    <section className="teacher-section" id="developers" ref={containerRef}>
      <div className="teacher-container">
        <motion.div
          initial={{ opacity: 0, y: -40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <MiniTitle>Dasturchilarimiz</MiniTitle>
          <MainTitle>Algoritm Dasturchilari</MainTitle>
        </motion.div>

        <div className="teacher-carousel-wrapper">
          <div className="swiper-centered-container">
            <Swiper
              ref={swiperRef}
              modules={[Autoplay]}
              loop={true}
              centeredSlides={true}
              slidesPerView={3}
              spaceBetween={40}
              speed={800}
              grabCursor={true}
              autoplay={false}
              onSlideChange={handleSlideChange}
              breakpoints={{
                320: { slidesPerView: 1.3, spaceBetween: 20 },
                640: { slidesPerView: 2.2 },
                768: { slidesPerView: 2.8 },
                1024: { slidesPerView: 3 },
                1280: { slidesPerView: 3, centeredSlides: true },
              }}
              className="teacher-swiper"
            >
              {developers.map((dev, index) => (
                <SwiperSlide key={dev.id}>
                  <DeveloperCard
                    dev={dev}
                    isActive={index === activeIndex}
                    onClick={() =>
                      swiperRef.current?.swiper?.slideToLoop(index)
                    }
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>

        <div className="teacher-info-wrapper">
          <button
            onClick={goPrev}
            className="info-nav-btn info-nav-btn-left"
            aria-label="Oldingi"
          >
            <FiArrowLeftCircle size={36} />
          </button>
          <button
            onClick={goNext}
            className="info-nav-btn info-nav-btn-right"
            aria-label="Keyingi"
          >
            <FiArrowRightCircle size={36} />
          </button>

          <div className="teacher-info-card">
            <div className="deco-circle deco-left"></div>
            <div className="deco-circle deco-right"></div>

            <AnimatePresence mode="wait">
              <motion.div
                key={activeIndex}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -30 }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
              >
                <h3 className="text-3xl font-bold mb-2 text-center md:text-left">
                  {current.fullName}
                </h3>
                <p className="teacher-position text-lg text-orange-600 mb-4 text-center md:text-left">
                  {current.position}
                </p>
                <p className="teacher-bio text-gray-700 leading-relaxed mb-8 text-center md:text-left">
                  {current.bio}
                </p>

                <div className="social-links justify-center md:justify-start">
                  <SocialIcon
                    href={current.linkedinUrl}
                    icon={<FaLinkedinIn />}
                    label="LinkedIn"
                  />
                  <SocialIcon
                    href={current.telegramUsername}
                    icon={<FaTelegramPlane />}
                    label="Telegram"
                  />
                  <SocialIcon
                    href={current.instagramUrl}
                    icon={<FaInstagram />}
                    label="Instagram"
                  />
                  <SocialIcon
                    href={current.gitUrl}
                    icon={<FaGithub />}
                    label="GitHub"
                  />
                  <PhoneTooltip phoneNumber={current.phoneNumber} />
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
});

Developers.displayName = "DevelopersSection";
export default Developers;
